import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  motion, 
  AnimatePresence, 
  useScroll, 
  useTransform,
  useSpring,
  useMotionValue
} from 'framer-motion';
import { 
  FaExternalLinkAlt, 
  FaGithub, 
  FaPlay, 
  FaChevronDown,
  FaArrowRight,
  FaEye,
  FaCode,
  FaPalette,
  FaMobile,
  FaDesktop,
  FaVrCardboard,
  FaRobot,
  FaLaptop,
  FaCog,
  FaCogs,
  FaRocket,
  FaLightbulb,
  FaHeart,
  FaBrain
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';

// Modern Hero Section with Dynamic Elements
const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: 
    linear-gradient(135deg, #ffffff 0%, #f8f9ff 20%, #ffffff 40%, #f5f7ff 60%, #ffffff 80%, #f0f4ff 100%),
    radial-gradient(circle at 25% 75%, rgba(0, 122, 255, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 75% 25%, rgba(255, 59, 48, 0.04) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(48, 209, 88, 0.03) 0%, transparent 50%);
  perspective: 1000px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 25% 25%, rgba(0, 122, 255, 0.03) 2px, transparent 2px),
      radial-gradient(circle at 75% 75%, rgba(255, 59, 48, 0.02) 1px, transparent 1px);
    background-size: 80px 80px, 60px 60px;
    opacity: 0.6;
    animation: patternFloat 30s linear infinite, patternMorph 12s ease-in-out infinite, patternPulse 8s ease-in-out infinite;
  }
  
  @keyframes patternFloat {
    0% { transform: translate(0, 0) scale(1) rotate(0deg); }
    20% { transform: translate(16px, -8px) scale(1.05) rotate(72deg); }
    40% { transform: translate(40px, -20px) scale(0.95) rotate(144deg); }
    60% { transform: translate(64px, 20px) scale(1.1) rotate(216deg); }
    80% { transform: translate(80px, 60px) scale(0.9) rotate(288deg); }
    100% { transform: translate(0, 0) scale(1) rotate(360deg); }
  }
  
  @keyframes patternMorph {
    0%, 100% { background-size: 80px 80px, 60px 60px; }
    25% { background-size: 70px 90px, 50px 70px; }
    50% { background-size: 90px 70px, 70px 50px; }
    75% { background-size: 75px 85px, 55px 65px; }
  }
  
  @keyframes patternPulse {
    0%, 100% { opacity: 0.6; }
    25% { opacity: 0.9; }
    50% { opacity: 0.4; }
    75% { opacity: 0.8; }
  }
`;

const HeroBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 10% 90%, rgba(0, 122, 255, 0.08) 0%, transparent 40%),
    radial-gradient(circle at 90% 10%, rgba(255, 59, 48, 0.06) 0%, transparent 40%),
    radial-gradient(circle at 30% 30%, rgba(48, 209, 88, 0.04) 0%, transparent 40%);
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const GlassElement = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: ${props => props.radius || '25px'};
  box-shadow: 
    0 10px 40px rgba(0, 0, 0, 0.1),
    inset 0 2px 0 rgba(255, 255, 255, 0.6);
`;

const ColorGlass = styled(motion.div)`
  position: absolute;
  background: ${props => props.gradient};
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${props => props.radius || '50%'};
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  text-align: center;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(3rem, 7vw, 7rem);
  font-weight: 900;
  margin-bottom: ${props => props.theme.spacing.lg};
  background: linear-gradient(135deg, 
    #1a1a1a 0%, 
    #007AFF 15%, 
    #5856D6 30%, 
    #FF3B30 45%, 
    #30D158 60%, 
    #FFD60A 75%, 
    #1a1a1a 100%);
  background-size: 600% 600%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: rainbowFlow 12s ease infinite;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  @keyframes rainbowFlow {
    0%, 100% { background-position: 0% 50%; }
    16.66% { background-position: 100% 0%; }
    33.33% { background-position: 100% 50%; }
    50% { background-position: 100% 100%; }
    66.66% { background-position: 50% 100%; }
    83.33% { background-position: 0% 100%; }
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: rgba(26, 26, 26, 0.8);
  margin-bottom: ${props => props.theme.spacing.xxl};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: ${props => props.theme.spacing.xxl};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: rgba(26, 26, 26, 0.6);
  cursor: pointer;
  
  svg {
    font-size: 1.5rem;
    animation: bounceDown 2s ease-in-out infinite;
  }
  
  @keyframes bounceDown {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25% { transform: translateY(5px) rotate(2deg); }
    50% { transform: translateY(10px) rotate(0deg); }
    75% { transform: translateY(5px) rotate(-2deg); }
  }
  
  &:hover {
    color: #007AFF;
    
    svg {
      animation-duration: 1s;
    }
  }
`;

// Portfolio Section with Advanced Filtering
const PortfolioSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const PortfolioContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const PortfolioHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxxl};
`;

const PortfolioTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
`;

const PortfolioDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 600px;
  margin: 0 auto ${props => props.theme.spacing.xxl};
  line-height: 1.6;
`;

const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.xxl};
  flex-wrap: wrap;
`;

const FilterTab = styled(motion.button)`
  background: ${props => props.active ? 
    'linear-gradient(135deg, #007AFF, #5856D6)' : 
    'rgba(255, 255, 255, 0.8)'};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  border: ${props => props.active ? 'none' : '1px solid rgba(0, 0, 0, 0.1)'};
  backdrop-filter: blur(20px);
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.radius.medium};
  font-weight: 600;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover:before {
    left: 100%;
  }
`;

const ProjectsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.radius.xlarge};
  overflow: hidden;
  position: relative;
  cursor: pointer;
  height: auto;
  min-height: 400px;
  max-height: 500px;
  transform-style: preserve-3d;
  display: flex;
  flex-direction: column;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      ${props => props.color1}15, 
      ${props => props.color2}08);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

const ProjectImage = styled(motion.div)`
  height: 200px;
  flex-shrink: 0;
  background: linear-gradient(135deg, ${props => props.color1}, ${props => props.color2});
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;

const ProjectOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.lg};
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const ProjectAction = styled(motion.button)`
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  svg {
    font-size: 1.2rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const ProjectContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;

const ProjectMeta = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const ProjectTag = styled.span`
  background: ${props => props.color};
  color: white;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.radius.small};
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
`;

const ProjectTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text};
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ProjectDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.5;
  font-size: 0.9rem;
  margin-bottom: ${props => props.theme.spacing.md};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  flex: 1;
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.xs};
  margin-top: auto;
`;

const TechTag = styled.span`
  background: rgba(0, 122, 255, 0.1);
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.radius.small};
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
`;

// Stats Section
const StatsSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: linear-gradient(180deg, 
    ${props => props.theme.colors.background} 0%, 
    ${props => props.theme.colors.backgroundAlt} 100%);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  max-width: 1000px;
  margin: 0 auto;
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
`;

const StatNumber = styled(motion.div)`
  font-size: 4rem;
  font-weight: 800;
  background: linear-gradient(135deg, #007AFF, #5856D6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const StatLabel = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textLight};
  font-weight: 500;
`;

// CTA Section
const CtaSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.backgroundDark};
  color: white;
  position: relative;
  overflow: hidden;
`;

const CtaBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(0, 122, 255, 0.2) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 59, 48, 0.1) 0%, transparent 50%);
`;

const CtaContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const CtaTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: white;
`;

const CtaDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: ${props => props.theme.spacing.xxl};
  line-height: 1.6;
`;

const CtaButton = styled(motion.button)`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xxl};
  border: none;
  border-radius: ${props => props.theme.radius.medium};
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  box-shadow: ${props => props.theme.shadows.large};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover:before {
    left: 100%;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
`;

// Magnetic Element Component
const MagneticElement = ({ children, strength = 0.3, ...props }) => {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 30 });
  const springY = useSpring(y, { stiffness: 300, damping: 30 });

  const handleMouseMove = (e) => {
    // Disable magnetic effect on mobile devices
    if (isMobile || !ref.current) return;
    
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) * strength);
    y.set((e.clientY - centerY) * strength);
  };

  const handleMouseLeave = () => {
    // Disable magnetic effect on mobile devices
    if (isMobile) return;
    
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      style={isMobile ? {} : { x: springX, y: springY }}
      onMouseMove={isMobile ? undefined : handleMouseMove}
      onMouseLeave={isMobile ? undefined : handleMouseLeave}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Counter Hook
const useCounter = (end, start = 0, duration = 2000) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, start, duration]);
  
  return [count, setIsVisible];
};

const StatCardComponent = ({ stat, index, setCursorVariant }) => {
  const [count, setIsVisible] = useCounter(stat.number);
  const ref = useRef(null);
  
  return (
    <StatCard
      ref={ref}
      initial={{ opacity: 0, scale: 0.5 }}
      whileInView={{ opacity: 1, scale: 1, transition: { 
        delay: index * 0.2, 
        duration: 0.8,
        onComplete: () => setIsVisible(true)
      }}}
      viewport={{ once: true }}
      whileHover={{ scale: 1.05 }}
      onMouseEnter={() => setCursorVariant("text")}
      onMouseLeave={() => setCursorVariant("default")}
    >
      <StatNumber>
        {count}{stat.suffix}
      </StatNumber>
      <StatLabel>{stat.label}</StatLabel>
    </StatCard>
  );
};

const Portfolio = ({ setCursorVariant }) => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [activeFilter, setActiveFilter] = useState('Tutti');
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    document.title = "Portfolio | D-Soft";
    window.scrollTo(0, 0);

    // Generate particles
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      color: `rgba(${Math.random() > 0.5 ? '0, 122, 255' : '255, 255, 255'}, ${Math.random() * 0.4 + 0.1})`,
      duration: Math.random() * 15 + 10,
      delay: Math.random() * 2
    }));
    setParticles(newParticles);
  }, []);

  const filters = ['Tutti', 'Applicazioni Web', 'App Mobile', 'Realtà Virtuale', 'Automazione', 'Sviluppo Web', 'Sviluppo Completo'];

  const projects = [
    {
      id: 1,
      title: "Weagle S.R.L - Motore di Ricerca Anti-Fake News",
      description: "Piattaforma di motore di ricerca robusta con React.js e Node.js, integrata con modello di machine learning per il contrasto delle fake news.",
      category: "Applicazioni Web",
      tech: ["React.js", "Node.js", "Machine Learning", "NLP"],
      color1: "#007AFF",
      color2: "#5856D6",
      tags: ["Sviluppo Web", "AI", "Sicurezza"]
    },
    {
      id: 2,
      title: "Uocy SRL - Esperienze VR/AR Immersive",
      description: "Tour virtuali coinvolgenti per città iconiche come Venezia, Matera e Civita utilizzando Unity e piattaforma Meta Quest.",
      category: "Realtà Virtuale",
      tech: ["Unity", "Meta Quest", "VR", "AR"],
      color1: "#30D158",
      color2: "#32D74B",
      tags: ["VR/AR", "Turismo", "Cultura"]
    },
    {
      id: 3,
      title: "Groove it SRL - Automazione Musicale",
      description: "Strumenti di automazione basati su Python per generare profili artistici e curare playlist dinamicamente per la produzione musicale.",
      category: "Automazione",
      tech: ["Python", "API Integration", "Data Parsing", "Template Generation"],
      color1: "#FF3B30",
      color2: "#FF9500",
      tags: ["Automazione", "Musica", "AI"]
    },
    {
      id: 4,
      title: "The Flat by Porsche Macan - Esperienza Biometrica",
      description: "Soluzione smartwatch per rilevare battito cardiaco e app Unity per elaborare dati biometrici con effetti particellari in tempo reale.",
      category: "App Mobile",
      tech: ["Unity", "Smartwatch SDK", "Biometric APIs", "Real-time Rendering"],
      color1: "#FFD60A",
      color2: "#FFCC02",
      tags: ["Biometria", "Gaming", "Real-time"]
    },
    {
      id: 5,
      title: "Lux SPA - Balloon Museum World",
      description: "Trasformazione del design Figma in sito web funzionante con Elementor e plugin WordPress personalizzato per gestione opere d'arte.",
      category: "Sviluppo Web",
      tech: ["WordPress", "Elementor", "PHP", "JavaScript", "API"],
      color1: "#8E44AD",
      color2: "#9B59B6",
      tags: ["WordPress", "Museum", "API"]
    },
    {
      id: 6,
      title: "MajorBit Innovation SRL - Soluzioni XR Complete",
      description: "Esperienze immersive XR con Unity, siti WordPress personalizzati e applicazioni React.js con backend .NET.",
      category: "Sviluppo Completo",
      tech: ["Unity", "React.js", ".NET", "WordPress", "XR"],
      color1: "#E74C3C",
      color2: "#C0392B",
      tags: ["XR", "Full-Stack", "Enterprise"]
    },
    {
      id: 7,
      title: "Fondazione Pasquinelli - Storia Musicale VR",
      description: "7 esperienze VR immersive per diversi periodi storici musicali, realizzate con Unreal Engine per video 360° ad alta fedeltà.",
      category: "Realtà Virtuale",
      tech: ["Unreal Engine", "Unity", "360° Video", "VR"],
      color1: "#6C5CE7",
      color2: "#A29BFE",
      tags: ["VR", "Educazione", "Storia"]
    },
    {
      id: 8,
      title: "Da Vinci srls - Cuz App Personalizzazione Adesivi",
      description: "App mobile React Native per personalizzare adesivi biciclette in tempo reale con backend Node.js e database MongoDB.",
      category: "App Mobile",
      tech: ["React Native", "Node.js", "MongoDB", "Real-time Preview"],
      color1: "#00B894",
      color2: "#00CEC9",
      tags: ["Mobile", "E-commerce", "Personalizzazione"]
    },
    {
      id: 9,
      title: "Panglossa srl - LMS Educazione Linguistica",
      description: "Sistema di gestione apprendimento personalizzato basato su WordPress per corsi di lingua online con e-commerce integrato.",
      category: "Sviluppo Web",
      tech: ["WordPress", "LMS", "E-commerce", "PHP", "JavaScript"],
      color1: "#0984E3",
      color2: "#74B9FF",
      tags: ["Educazione", "LMS", "E-learning"]
    }
  ];

  const stats = [
    { number: 50, label: "Progetti Completati", suffix: "+" },
    { number: 35, label: "Clienti Soddisfatti", suffix: "+" },
    { number: 98, label: "Tasso di Successo", suffix: "%" },
    { number: 24, label: "Premi Vinti", suffix: "" }
  ];

  const filteredProjects = activeFilter === 'Tutti' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <div>
      {/* Hero Section */}
      <HeroSection ref={heroRef}>
        <HeroBackground style={{ y: parallaxY, opacity }} />
        
        <FloatingElements>
          {/* Primary Glass Elements */}
          <GlassElement
            radius="45px"
            animate={{
              x: [0, 130, -90, 0],
              y: [0, -75, 85, 0],
              rotate: [0, 150, 300, 360],
              scale: [1, 1.15, 0.85, 1],
            }}
            transition={{
              duration: 35,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '240px',
              height: '240px',
              top: '10%',
              left: '12%',
            }}
          />
          
          <GlassElement
            radius="50%"
            animate={{
              x: [0, -140, 110, 0],
              y: [0, 90, -70, 0],
              rotate: [0, -180, -360],
              scale: [1, 0.9, 1.1, 1],
            }}
            transition={{
              duration: 40,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{
              width: '200px',
              height: '200px',
              top: '70%',
              right: '10%',
            }}
          />

          {/* Colored Glass Shapes */}
          <ColorGlass
            gradient="linear-gradient(135deg, rgba(0, 122, 255, 0.18) 0%, rgba(88, 86, 214, 0.09) 100%)"
            radius="35px"
            animate={{
              x: [0, 90, -45, 0],
              y: [0, -65, 75, 0],
              rotate: [0, 200, 400],
            }}
            transition={{
              duration: 27,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '150px',
              height: '150px',
              top: '5%',
              right: '18%',
            }}
          />
          
          <ColorGlass
            gradient="linear-gradient(135deg, rgba(255, 59, 48, 0.15) 0%, rgba(255, 149, 0, 0.08) 100%)"
            radius="50%"
            animate={{
              x: [0, -100, 70, 0],
              y: [0, 85, -55, 0],
              scale: [1, 1.25, 0.75, 1],
              rotate: [0, 120, 240, 360],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '130px',
              height: '130px',
              top: '50%',
              left: '8%',
            }}
          />
          
          <ColorGlass
            gradient="linear-gradient(135deg, rgba(48, 209, 88, 0.15) 0%, rgba(50, 215, 75, 0.08) 100%)"
            radius="25px"
            animate={{
              x: [0, 75, -40, 0],
              y: [0, -50, 70, 0],
              rotate: [0, 90, 180, 270, 360],
              scale: [1, 1.1, 0.9, 1],
            }}
            transition={{
              duration: 24,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '110px',
              height: '110px',
              top: '80%',
              right: '30%',
            }}
          />
          
          <ColorGlass
            gradient="linear-gradient(135deg, rgba(255, 204, 0, 0.15) 0%, rgba(255, 214, 10, 0.08) 100%)"
            radius="40px"
            animate={{
              x: [0, 120, -60, 0],
              y: [0, -80, 100, 0],
              rotate: [0, -135, -270, -405],
            }}
            transition={{
              duration: 33,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '170px',
              height: '170px',
              top: '30%',
              right: '5%',
            }}
          />
          
          <ColorGlass
            gradient="linear-gradient(135deg, rgba(88, 86, 214, 0.15) 0%, rgba(0, 122, 255, 0.08) 100%)"
            radius="50%"
            animate={{
              x: [0, -85, 95, 0],
              y: [0, 75, -85, 0],
              scale: [1, 0.8, 1.2, 1],
            }}
            transition={{
              duration: 28,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '125px',
              height: '125px',
              top: '15%',
              left: '65%',
            }}
          />

          {/* Complex Geometric Patterns */}
          <motion.div
            style={{
              position: 'absolute',
              width: '350px',
              height: '350px',
              top: '20%',
              right: '12%',
              background: `
                conic-gradient(from 45deg, 
                  rgba(0, 122, 255, 0.06) 0deg,
                  rgba(255, 59, 48, 0.04) 60deg,
                  rgba(48, 209, 88, 0.05) 120deg,
                  rgba(255, 204, 0, 0.04) 180deg,
                  rgba(88, 86, 214, 0.05) 240deg,
                  rgba(255, 149, 0, 0.04) 300deg,
                  rgba(0, 122, 255, 0.06) 360deg)
              `,
              borderRadius: '50%',
              opacity: 0.7,
            }}
            animate={{
              rotate: [0, 360],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 50,
              repeat: Infinity,
              ease: "linear",
            }}
          />

          {/* Mesh Pattern */}
          <motion.div
            style={{
              position: 'absolute',
              width: '450px',
              height: '100px',
              top: '45%',
              left: '8%',
              background: `
                repeating-conic-gradient(from 0deg at 50% 50%,
                  rgba(0, 122, 255, 0.04) 0deg,
                  transparent 15deg,
                  rgba(255, 59, 48, 0.03) 30deg,
                  transparent 45deg,
                  rgba(48, 209, 88, 0.04) 60deg,
                  transparent 75deg,
                  rgba(0, 122, 255, 0.04) 90deg)
              `,
              borderRadius: '50px',
              opacity: 0.8,
            }}
            animate={{
              x: [0, 60, -40, 0],
              rotateY: [0, 180, 360],
              scaleX: [1, 1.1, 1],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Floating Orbs */}
          <motion.div
            style={{
              position: 'absolute',
              width: '250px',
              height: '250px',
              top: '8%',
              right: '35%',
              background: `
                radial-gradient(circle at 30% 30%, rgba(0, 122, 255, 0.2) 4px, transparent 4px),
                radial-gradient(circle at 70% 70%, rgba(255, 59, 48, 0.15) 3px, transparent 3px),
                radial-gradient(circle at 50% 80%, rgba(48, 209, 88, 0.18) 2px, transparent 2px)
              `,
              backgroundSize: '40px 40px, 35px 35px, 30px 30px',
              borderRadius: '50%',
              opacity: 0.6,
            }}
            animate={{
              x: [0, 40, -25, 0],
              y: [0, -35, 50, 0],
              rotate: [0, 180, 360],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 35,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </FloatingElements>

        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            onMouseEnter={() => setCursorVariant("text")}
            onMouseLeave={() => setCursorVariant("default")}
          >
            Il Nostro Portfolio
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            Scopri i nostri progetti innovativi e le soluzioni digitali che guidano il successo
          </HeroSubtitle>
        </HeroContent>

        <ScrollIndicator
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          onClick={scrollToContent}
          onMouseEnter={() => setCursorVariant("button")}
          onMouseLeave={() => setCursorVariant("default")}
        >
          <span>Esplora il Nostro Lavoro</span>
          <FaChevronDown />
        </ScrollIndicator>
      </HeroSection>

      {/* Portfolio Section */}
      <PortfolioSection>
        <PortfolioContainer>
          <PortfolioHeader>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <PortfolioTitle
                onMouseEnter={() => setCursorVariant("text")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                Lavori in Evidenza
              </PortfolioTitle>
              
              <PortfolioDescription>
                Ogni progetto rappresenta il nostro impegno per l'eccellenza, l'innovazione e 
                la realizzazione di soluzioni che hanno un impatto reale.
              </PortfolioDescription>
            </motion.div>
          </PortfolioHeader>

          <FilterTabs>
            {filters.map((filter) => (
              <FilterTab
                key={filter}
                active={activeFilter === filter}
                onClick={() => setActiveFilter(filter)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={() => setCursorVariant("button")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                {filter}
              </FilterTab>
            ))}
          </FilterTabs>

          <AnimatePresence mode="wait">
            <ProjectsGrid
              key={activeFilter}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.5 }}
            >
              {filteredProjects.map((project, index) => (
                <ProjectCard
                  key={project.id}
                  color1={project.color1}
                  color2={project.color2}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ 
                    scale: 1.01,
                    transition: { duration: 0.2 }
                  }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  onMouseEnter={() => setCursorVariant("link")}
                  onMouseLeave={() => setCursorVariant("default")}
                >
                  <ProjectImage color1={project.color1} color2={project.color2}>
                    <ProjectOverlay 
                      className="project-overlay"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                    >
                      <ProjectAction
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaEye />
                      </ProjectAction>
                      <ProjectAction
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaExternalLinkAlt />
                      </ProjectAction>
                      <ProjectAction
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaGithub />
                      </ProjectAction>
                    </ProjectOverlay>
                  </ProjectImage>
                  
                  <ProjectContent>
                    <div>
                      <ProjectMeta>
                        {project.tags.map((tag, i) => (
                          <ProjectTag key={i} color={i === 0 ? project.color1 : project.color2}>
                            {tag}
                          </ProjectTag>
                        ))}
                      </ProjectMeta>
                      
                      <ProjectTitle>{project.title}</ProjectTitle>
                      <ProjectDescription>{project.description}</ProjectDescription>
                    </div>
                    
                    <ProjectTech>
                      {project.tech.map((tech, i) => (
                        <TechTag key={i}>{tech}</TechTag>
                      ))}
                    </ProjectTech>
                  </ProjectContent>
                </ProjectCard>
              ))}
            </ProjectsGrid>
          </AnimatePresence>
        </PortfolioContainer>
      </PortfolioSection>

      {/* Stats Section */}
      <StatsSection>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center', marginBottom: '4rem' }}
          >
            <h2 
              style={{ fontSize: '3rem', marginBottom: '1rem' }}
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              I Nostri Risultati
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#86868B' }}>
              Numeri che riflettono la nostra dedizione all'eccellenza
            </p>
          </motion.div>

          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCardComponent key={index} stat={stat} index={index} setCursorVariant={setCursorVariant} />
            ))}
          </StatsGrid>
        </div>
      </StatsSection>

      {/* CTA Section */}
      <CtaSection>
        <CtaBackground />
        <CtaContent>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <CtaTitle
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Pronto a Iniziare il Tuo Progetto?
            </CtaTitle>
            
            <CtaDescription>
              Collaboriamo per dare vita alla tua visione con design innovativo 
              e tecnologia all'avanguardia.
            </CtaDescription>
            
            <Link to="/contatti">
              <CtaButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={() => setCursorVariant("button")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                Inizia il Tuo Progetto
                <FaArrowRight />
              </CtaButton>
            </Link>
          </motion.div>
        </CtaContent>
      </CtaSection>
    </div>
  );
};

export default Portfolio; 